import gql from 'graphql-tag'

export interface IContentFeedTopic {
  id: string
  name: string
}

export interface IContentFeed {
  id: string
  name: string
  slug: string
  description: string | null
  displayOrder: number | null
  enabled: boolean
  /** Items curated into the feed, for showing capacity used. */
  boostCount: number
  topics: IContentFeedTopic[]
}

export interface IContentFeedsResponse {
  contentFeeds: IContentFeed[]
}

/**
 * Always called with includeDisabled: true. A curator has to see disabled feeds
 * in order to configure them, and the server gates that argument on curation
 * rights -- which this whole section already requires.
 */
export const contentFeedsQuery = gql`
  query contentFeeds($includeDisabled: Boolean) {
    contentFeeds(includeDisabled: $includeDisabled) {
      id
      name
      slug
      description
      displayOrder
      enabled
      boostCount
      topics {
        id
        name
      }
    }
  }
`

export interface IContentFeedResponse {
  contentFeed: IContentFeed | null
}

export const contentFeedQuery = gql`
  query contentFeed($id: String!) {
    contentFeed(id: $id) {
      id
      name
      slug
      description
      displayOrder
      enabled
      boostCount
      topics {
        id
        name
      }
    }
  }
`

export interface IContentFeedItemImage {
  cloudinaryId: string
}

/**
 * The Media | Document | Doclet union, flattened to the fields the three share.
 *
 * Two fields cannot be selected uniformly. Media and Document have a single
 * `image`, a Doclet has `images`. And Doclet.subtitle is String! where the other
 * two are String -- selecting both under one response name is an illegal merge,
 * so the Doclet's is aliased. Read them through subtitleOf/cloudinaryIdOf.
 */
export interface IContentFeedItemTopic {
  id: string
  name: string
}

export interface IContentFeedItem {
  __typename: 'Media' | 'Document' | 'Doclet'
  id: string
  title: string
  subtitle?: string | null
  docletSubtitle?: string | null
  publishDate: string | null
  published: boolean
  image?: IContentFeedItemImage | null
  images?: IContentFeedItemImage[] | null
  topics?: IContentFeedItemTopic[] | null
  contentTopics?: IContentFeedItemTopic[] | null
  /** Media only. */
  category?: string | null
  hostMedia?: {
    id: string
    duration: number | null
    type: string | null
  } | null
  /** Document only -- aliased because Doclet.type is a different enum. */
  documentType?: string | null
  /** Doclet only. */
  docletType?: string | null
}

/**
 * Watch / Listen / Read. Only Media distinguishes audio from video, via
 * hostMedia.type; a Document or Doclet is always read.
 */
export const verbKeyOf = (item: IContentFeedItem): string => {
  if (item.__typename !== 'Media') return 'read'
  return item.hostMedia?.type === 'audio' ? 'listen' : 'watch'
}

/**
 * Runtime in whole minutes. Media only: HostMedia.duration is the sole length
 * field in the schema, stored in seconds. Documents have no page count anywhere
 * -- not on Document, not on FileAttachment -- so their length slot stays empty
 * until the backend records one.
 */
export const durationMinutesOf = (item: IContentFeedItem): number | null => {
  const seconds = item.hostMedia?.duration
  if (!seconds) return null
  // Round up rather than to nearest, so a 40-second clip is not "0 min".
  return Math.max(1, Math.round(seconds / 60))
}

/**
 * i18n key for the format label. DocletType's values are all present in
 * DocumentType, so both reuse the library:document:types namespace rather than
 * duplicating fourteen strings.
 */
export const formatKeyOf = (item: IContentFeedItem): string | null => {
  if (item.__typename === 'Media') {
    return item.category ? `media:category:${item.category}` : null
  }
  const type =
    item.__typename === 'Document' ? item.documentType : item.docletType
  return type ? `library:document:types:${type}` : null
}

export const subtitleOf = (item: IContentFeedItem): string | null =>
  item.subtitle ?? item.docletSubtitle ?? null

export const cloudinaryIdOf = (item: IContentFeedItem): string | null =>
  item.image?.cloudinaryId ?? item.images?.[0]?.cloudinaryId ?? null

export const topicsOf = (item: IContentFeedItem): IContentFeedItemTopic[] =>
  item.topics ?? item.contentTopics ?? []

export interface IContentFeedBoost {
  rank: number
  itemType: string
  itemId: string
  expiresAt: string | null
  /** Null when the item was hard deleted out from under the boost. */
  item: IContentFeedItem | null
}

export interface IContentFeedBoostsResponse {
  contentFeedBoosts: IContentFeedBoost[]
}

/** Shared by the boosts query and the setContentFeedBoosts mutation. */
export const contentFeedItemFragment = gql`
  fragment ContentFeedItemFields on ContentFeedItem {
    __typename
    ... on Media {
      id
      title
      subtitle
      publishDate
      published
      category
      # duration is seconds; type is audio | video, which decides Watch vs Listen
      hostMedia {
        id
        duration
        type
      }
      image {
        cloudinaryId
      }
      topics {
        id
        name
      }
    }
    ... on Document {
      id
      title
      subtitle
      publishDate
      published
      # Document.type and Doclet.type are different enums, so they cannot share
      # a response name any more than subtitle can.
      documentType: type
      image {
        cloudinaryId
      }
      topics {
        id
        name
      }
    }
    ... on Doclet {
      id
      title
      # Doclet.subtitle is String! where Media's and Document's are String, so
      # it cannot share their response name.
      docletSubtitle: subtitle
      publishDate
      published
      docletType: type
      images {
        cloudinaryId
      }
      # A Doclet's live on contentTopics, not topics -- its topics field is the
      # older Topic type, a different thing entirely. No backticks in here:
      # this is inside a template literal and they would close it.
      contentTopics {
        id
        name
      }
    }
  }
`

/**
 * Needs curation rights, which this whole section already requires. Unlike
 * ContentFeed.entries this is not behind the topicContentFeed feature flag,
 * because it reads the curated list rather than the member-facing feed.
 */
export const contentFeedBoostsQuery = gql`
  query contentFeedBoosts($contentFeedId: String!) {
    contentFeedBoosts(contentFeedId: $contentFeedId) {
      rank
      itemType
      itemId
      expiresAt
      item {
        ...ContentFeedItemFields
      }
    }
  }
  ${contentFeedItemFragment}
`
